import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { 
  BookOpen, 
  Zap, 
  Brain, 
  Target, 
  TrendingUp,
  Award,
  Eye,
  Timer,
  Send,
  BarChart3,
  CheckCircle2
} from 'lucide-react';

// Feature Data - OkurAtölyesi'ne özel
const features = [
  {
    id: 0,
    title: "Okuma Hızınızı\nKatlayın",
    description: "Bilimsel tekniklerle dakikada okuduğunuz kelime sayısını 2-3 katına çıkarın. Anlama oranınızı koruyarak hızınızı artırın.",
    tags: ['Hız Testi', 'Kişisel Program', 'Günlük Egzersiz', 'İlerleme Takibi']
  },
  {
    id: 1,
    title: "Göz Kaslarınızı\nGüçlendirin",
    description: "Göz koordinasyonu ve odaklanma egzersizleriyle okuma kaslarınızı geliştirin. Yorulmadan daha uzun süre okuyun.",
    tags: ['Göz Egzersizi', 'Odaklanma', 'Çevresel Görüş', 'Kas Güçlendirme']
  },
  {
    id: 2,
    title: "İlerlemenizi\nGörün",
    description: "Detaylı istatistikler ve analizlerle gelişiminizi takip edin. Haftalık hedefler belirleyin ve başarılarınızı kutlayın.",
    tags: ['Detaylı Analiz', 'Haftalık Rapor', 'Başarı Rozetleri', 'Karşılaştırma']
  },
  {
    id: 3,
    title: "Bilge AI\nAsistanınız",
    description: "Yapay zeka destekli kişisel koçunuz ile öğrenme sürecinizi optimize edin. Size özel tavsiyeler ve stratejiler alın.",
    tags: ['AI Koç', 'Kişisel Tavsiye', 'Akıllı Analiz', '7/24 Destek']
  }
];

// ============================================
// VISUAL 1: Okuma Hızı Metrikleri
// ============================================
const SpeedMeterVisual = () => {
  const wpm = useMotionValue(0);
  const roundedWpm = useTransform(wpm, (latest) => Math.round(latest));
  const [displayWpm, setDisplayWpm] = useState(0);

  useEffect(() => {
    const controls = animate(wpm, 485, {
      duration: 2.5,
      ease: "easeOut",
    });

    const unsubscribe = roundedWpm.on("change", (latest) => {
      setDisplayWpm(latest);
    });

    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [wpm, roundedWpm]);

  return (
    <div className="relative h-full w-full flex items-center justify-center">
      {/* Ana Hız Göstergesi */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="relative"
      >
        {/* Dış Halka - Animasyonlu */}
        <svg className="w-72 h-72 xl:w-80 xl:h-80" viewBox="0 0 200 200">
          <defs>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Arka plan halkası */}
          <circle
            cx="100"
            cy="100"
            r="85"
            fill="none"
            stroke="currentColor"
            strokeWidth="12"
            className="text-gray-200 dark:text-gray-800"
            strokeLinecap="round"
          />
          
          {/* İlerleme halkası */}
          <motion.circle
            cx="100"
            cy="100"
            r="85"
            fill="none"
            stroke="#8B5CF6"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray="534"
            filter="url(#glow)"
            initial={{ strokeDashoffset: 534 }}
            animate={{ strokeDashoffset: 534 - (534 * 0.75) }}
            transition={{ duration: 2.5, ease: "easeOut" }}
            style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
          />
        </svg>

        {/* Merkez İçerik */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl xl:text-6xl font-bold text-violet-600 dark:text-violet-400">
                {displayWpm}
              </span>
              <span className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                k/dk
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Okuma Hızınız
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Stats */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
        className="absolute left-0 top-1/4 p-4 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-xl border border-white/20 dark:border-gray-700/50"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-green-100 dark:bg-green-900/30">
            <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Bu Hafta</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">+127%</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1 }}
        className="absolute right-0 bottom-1/4 p-4 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-xl border border-white/20 dark:border-gray-700/50"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-violet-100 dark:bg-violet-900/30">
            <BookOpen className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Okunan</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">24 Kitap</p>
          </div>
        </div>
      </motion.div>

      {/* Animated Particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full bg-violet-500"
          initial={{ 
            x: 0, 
            y: 0, 
            scale: 0,
            opacity: 0 
          }}
          animate={{ 
            x: [0, ((i % 2 === 0 ? 1 : -1) * (50 + i * 20))],
            y: [0, ((i % 3 === 0 ? 1 : -1) * (50 + i * 15))],
            scale: [0, 1, 0],
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: 3,
            delay: i * 0.5,
            repeat: Infinity,
            repeatDelay: 1
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// VISUAL 2: Göz Egzersizi Animasyonu
// ============================================
const EyeExerciseVisual = () => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhase((p) => (p + 1) % 4);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  // Göz pozisyonları - kare hareket
  const positions = [
    { x: -60, y: -60 },  // Sol üst
    { x: 60, y: -60 },   // Sağ üst
    { x: 60, y: 60 },    // Sağ alt
    { x: -60, y: 60 },   // Sol alt
  ];

  return (
    <div className="relative h-full w-full flex items-center justify-center">
      {/* Ana Göz Alanı */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative w-72 h-72 xl:w-80 xl:h-80"
      >
        {/* Dış çerçeve */}
        <div className="absolute inset-0 rounded-3xl border-2 border-dashed border-gray-300 dark:border-gray-700" />

        {/* Köşe noktaları */}
        {positions.map((pos, i) => (
          <motion.div
            key={i}
            className={`absolute w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300
              ${phase === i 
                ? 'bg-violet-500 scale-125' 
                : 'bg-gray-200 dark:bg-gray-700'
              }`}
            style={{
              left: `calc(50% + ${pos.x}px - 12px)`,
              top: `calc(50% + ${pos.y}px - 12px)`,
            }}
          >
            {phase === i && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 0.5 }}
                className="w-2 h-2 rounded-full bg-white"
              />
            )}
          </motion.div>
        ))}

        {/* Merkez göz ikonu */}
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          animate={{
            x: positions[phase].x * 0.3,
            y: positions[phase].y * 0.3,
          }}
          transition={{ type: "spring", stiffness: 100, damping: 15 }}
        >
          <div className="relative">
            {/* Göz dış çerçeve */}
            <div className="w-24 h-24 rounded-full bg-white dark:bg-gray-800 shadow-2xl flex items-center justify-center border-4 border-gray-100 dark:border-gray-700">
              {/* Göz bebeği */}
              <motion.div
                className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center"
                animate={{
                  x: positions[phase].x * 0.15,
                  y: positions[phase].y * 0.15,
                }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
              >
                <div className="w-4 h-4 rounded-full bg-white/80" />
              </motion.div>
            </div>
            
            {/* Glow effect */}
            <motion.div
              className="absolute inset-0 rounded-full bg-violet-500/20 blur-xl -z-10"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>

        {/* Hareket çizgisi */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 320">
          <motion.path
            d="M 100,100 L 220,100 L 220,220 L 100,220 Z"
            fill="none"
            stroke="#8B5CF6"
            strokeWidth="2"
            strokeDasharray="480"
            initial={{ strokeDashoffset: 480 }}
            animate={{ strokeDashoffset: 480 - (phase + 1) * 120 }}
            transition={{ duration: 0.5 }}
          />
        </svg>
      </motion.div>

      {/* Sağ üst bilgi kartı */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="absolute top-8 right-4 p-3 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-lg border border-white/20 dark:border-gray-700/50"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Kare Takip
          </span>
        </div>
      </motion.div>

      {/* Sol alt progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="absolute bottom-8 left-4 p-4 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-lg border border-white/20 dark:border-gray-700/50"
      >
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Egzersiz</p>
        <div className="flex gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-8 h-1.5 rounded-full transition-all duration-300 ${
                i <= phase 
                  ? 'bg-violet-500' 
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// VISUAL 3: İlerleme ve İstatistikler
// ============================================
const ProgressVisual = () => {
  const [activeDay, setActiveDay] = useState(6);
  
  const weekData = [
    { day: 'Pzt', value: 45, target: 30 },
    { day: 'Sal', value: 62, target: 30 },
    { day: 'Çar', value: 38, target: 30 },
    { day: 'Per', value: 85, target: 30 },
    { day: 'Cum', value: 52, target: 30 },
    { day: 'Cmt', value: 74, target: 30 },
    { day: 'Paz', value: 90, target: 30 },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveDay((d) => (d + 1) % 7);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-full w-full flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/20 dark:border-gray-700/50"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Haftalık Özet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Bu hafta harika gidiyorsun!</p>
          </div>
          <div className="p-2 rounded-xl bg-violet-500">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Chart */}
        <div className="flex items-end justify-between gap-2 h-32 mb-4">
          {weekData.map((data, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <motion.div
                className={`w-full rounded-t-lg transition-all duration-500 ${
                  i === activeDay
                    ? 'bg-violet-500'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
                initial={{ height: 0 }}
                animate={{ height: `${data.value}%` }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
              />
              <span className={`text-xs transition-colors ${
                i === activeDay 
                  ? 'text-violet-600 dark:text-violet-400 font-bold' 
                  : 'text-gray-400 dark:text-gray-500'
              }`}>
                {data.day}
              </span>
            </div>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <motion.div 
            className="p-3 rounded-xl bg-violet-50 dark:bg-violet-900/20"
            whileHover={{ scale: 1.05 }}
          >
            <Timer className="w-4 h-4 text-violet-600 dark:text-violet-400 mb-1" />
            <p className="text-lg font-bold text-violet-600 dark:text-violet-400">4.2h</p>
            <p className="text-[10px] text-violet-600/70 dark:text-violet-400/70">Toplam</p>
          </motion.div>
          <motion.div 
            className="p-3 rounded-xl bg-pink-50 dark:bg-pink-900/20"
            whileHover={{ scale: 1.05 }}
          >
            <Target className="w-4 h-4 text-pink-600 dark:text-pink-400 mb-1" />
            <p className="text-lg font-bold text-pink-600 dark:text-pink-400">92%</p>
            <p className="text-[10px] text-pink-600/70 dark:text-pink-400/70">Hedef</p>
          </motion.div>
          <motion.div 
            className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20"
            whileHover={{ scale: 1.05 }}
          >
            <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400 mb-1" />
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">7</p>
            <p className="text-[10px] text-amber-600/70 dark:text-amber-400/70">Seri</p>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Achievement Badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0, rotate: -180 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ delay: 1, type: "spring" }}
        className="absolute -top-2 -right-2 p-4 rounded-2xl bg-amber-500 shadow-xl"
      >
        <Award className="w-8 h-8 text-white" />
        <motion.div
          className="absolute inset-0 rounded-2xl bg-white/30"
          animate={{ opacity: [0, 0.5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>

      {/* Streak Fire */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="absolute bottom-4 left-4 flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white shadow-lg"
      >
        <motion.span
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="text-lg"
        >
          🔥
        </motion.span>
        <span className="text-sm font-bold">7 Gün Seri!</span>
      </motion.div>
    </div>
  );
};

// ============================================
// VISUAL 4: AI Asistan - Bilge
// ============================================
const AIAssistantVisual = () => {
  const [typingIndex, setTypingIndex] = useState(0);
  const message = "Bugün göz egzersizlerine odaklanalım. Geçen hafta %23 gelişme gösterdin!";
  
  useEffect(() => {
    if (typingIndex < message.length) {
      const timeout = setTimeout(() => {
        setTypingIndex(typingIndex + 1);
      }, 30);
      return () => clearTimeout(timeout);
    }
  }, [typingIndex, message.length]);

  return (
    <div className="relative h-full w-full flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xs overflow-hidden rounded-2xl bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl shadow-2xl border border-white/20 dark:border-gray-700/50"
      >
        {/* Header */}
        <div className="bg-violet-600 p-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-violet-600" />
            </div>
            <div className="text-white">
              <h3 className="text-sm font-bold">Bilge</h3>
              <p className="text-xs text-white/80">AI Öğrenme Koçunuz</p>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="p-3 space-y-3">
          {/* User Message */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="ml-auto max-w-[85%]"
          >
            <div className="rounded-xl rounded-tr-sm bg-violet-100 dark:bg-violet-900/30 px-3 py-2">
              <p className="text-xs text-violet-900 dark:text-violet-100">
                Bugün ne çalışmalıyım?
              </p>
            </div>
          </motion.div>

          {/* AI Response */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="max-w-[90%]"
          >
            <div className="rounded-xl rounded-tl-sm bg-gray-100 dark:bg-gray-700/50 px-3 py-2">
              <p className="text-xs text-gray-800 dark:text-gray-200">
                {message.slice(0, typingIndex)}
                {typingIndex < message.length && (
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity }}
                    className="inline-block w-0.5 h-3 bg-violet-500 ml-0.5 align-middle"
                  />
                )}
              </p>
              
              {typingIndex >= message.length && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                      <Eye className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-white">Göz Egzersizi</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">15 dk • Başlangıç</p>
                    </div>
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Input Area */}
        <div className="p-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/30">
            <input
              type="text"
              placeholder="Bilge'ye sor..."
              className="flex-1 bg-transparent text-xs outline-none text-gray-700 dark:text-gray-300 placeholder-gray-400"
              readOnly
            />
            <button className="p-1.5 rounded-md bg-violet-600">
              <Send className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ============================================
// VISUAL ARRAY
// ============================================
const visuals = [
  SpeedMeterVisual,
  EyeExerciseVisual,
  ProgressVisual,
  AIAssistantVisual
];

// ============================================
// MAIN COMPONENT
// ============================================
export const AuthHero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const duration = 5000;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % features.length);
    }, duration);

    return () => clearInterval(timer);
  }, []);

  const CurrentVisual = visuals[currentIndex];

  return (
    <div className="relative hidden h-full w-full overflow-hidden lg:flex flex-col transition-colors duration-500">
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        {/* Base background */}
        <div className="absolute inset-0 bg-gray-50 dark:bg-gray-900" />
        
        {/* Animated blobs */}
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -20, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -left-32 -top-32 w-96 h-96 rounded-full bg-violet-400/15 dark:bg-violet-600/10 blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            x: [0, -20, 0],
            y: [0, 30, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full bg-violet-400/15 dark:bg-violet-600/10 blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.15, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 4 }}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-violet-400/10 dark:bg-violet-600/5 blur-3xl" 
        />
      </div>

      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 z-[1] opacity-[0.015] dark:opacity-[0.03]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-8 pt-8 pb-4">
        
        {/* Animated Visual Area */}
        <div className="relative mb-6 h-[360px] w-full max-w-[400px] xl:h-[400px] xl:max-w-[450px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <CurrentVisual />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Text Content */}
        <div className="w-full max-w-lg text-center min-h-[180px] relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="mb-4 text-2xl font-bold leading-tight text-gray-900 dark:text-white md:text-3xl xl:text-4xl whitespace-pre-line">
                {features[currentIndex].title}
              </h2>

              <p className="mb-6 text-base leading-relaxed text-gray-600 dark:text-gray-400 xl:text-lg px-4">
                {features[currentIndex].description}
              </p>

              {/* Feature Tags */}
              <div className="flex flex-wrap justify-center gap-2 xl:gap-3">
                {features[currentIndex].tags.map((tag, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className="rounded-full border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 px-4 py-1.5 text-xs font-medium text-violet-700 dark:text-violet-300 xl:text-sm"
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="relative z-20 flex justify-center gap-3 pb-10">
        {features.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800 transition-all hover:bg-gray-300 dark:hover:bg-gray-700"
          >
            {i === currentIndex && (
              <motion.div
                className="h-full bg-violet-600"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: duration / 1000, ease: "linear" }}
              />
            )}
            {i < currentIndex && (
              <div className="h-full w-full bg-violet-600" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
